<template>
  <div class="ledger-page pb-5 pb-md-2">
    <div
      v-if="loading"
      class="text-center py-3"
    >
      <b-spinner variant="primary" />
    </div>
    <template v-else-if="invoice">
      <b-card class="rounded-lg">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-50">
              {{ invoice.invoice_number }}
            </h3>
            <div>{{ customerName }}</div>
            <div class="small text-muted">
              {{ $t('ledger_gl_account') }}: {{ customerGlCode }}
            </div>
            <div class="text-muted small">
              {{ projectLabel }} · {{ activityLabel }}
              <span v-if="invoice.revenue_code"> · {{ invoice.revenue_code }}</span>
            </div>
            <b-badge
              class="mt-50"
              :variant="statusVariant"
            >
              {{ enumLabel(invoice.status, $i18n.locale) }}
            </b-badge>
            <b-badge
              v-if="kindValue && kindValue !== 'standard'"
              variant="primary"
              class="mt-50 ml-50"
            >
              {{ enumLabel(invoice.kind, $i18n.locale) }}
            </b-badge>
            <b-badge
              v-if="invoice.is_overdue"
              variant="danger"
              class="mt-50 ml-50"
            >
              {{ $t('ledger_inv_overdue') }}
            </b-badge>
          </div>
          <div class="d-none d-md-flex flex-wrap">
            <b-button
              v-if="canEditDraft"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="outline-primary"
              :to="{ name: 'ledger-invoices-edit', params: { id: invoice.id } }"
            >
              {{ $t('ledger_inv_edit') }}
            </b-button>
            <b-button
              v-if="canDelete"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="outline-danger"
              @click="confirmDelete"
            >
              {{ $t('ledger_assets_delete') }}
            </b-button>
            <b-button
              v-if="canIssue"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="primary"
              @click="confirmIssue"
            >
              {{ $t('ledger_inv_issue') }}
            </b-button>
            <b-button
              v-if="canCollect"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="success"
              @click="payOpen = true"
            >
              {{ $t('ledger_inv_collect') }}
            </b-button>
            <b-button
              v-if="canCollect"
              size="sm"
              class="mr-50 mb-50 rounded-pill"
              variant="outline-primary"
              :disabled="advanceBalance <= 0"
              @click="openAdvance"
            >
              {{ $t('ledger_inv_apply_advance') }}
            </b-button>
            <b-button
              v-if="canCancel"
              size="sm"
              class="mb-50 rounded-pill"
              variant="outline-danger"
              @click="confirmCancel"
            >
              {{ $t('ledger_inv_cancel') }}
            </b-button>
          </div>
        </div>
        <div class="ledger-inv-kpis mt-1">
          <div><span class="text-muted">{{ $t('ledger_inv_total') }}</span><strong class="ledger-num d-block">{{ money(invoice.total_amount) }}</strong></div>
          <div v-if="kindValue === 'progress_billing'"><span class="text-muted">{{ $t('ledger_ct_retention') }}</span><strong class="ledger-num d-block">{{ money(invoice.retention_amount) }}</strong></div>
          <div v-if="kindValue === 'progress_billing'"><span class="text-muted">{{ $t('ledger_ct_net') }}</span><strong class="ledger-num d-block">{{ money(invoice.collectible_amount) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_inv_paid') }}</span><strong class="ledger-num d-block">{{ money(invoice.paid_amount) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_inv_advance_applied') }}</span><strong class="ledger-num d-block">{{ money(invoice.advance_applied) }}</strong></div>
          <div><span class="text-muted">{{ $t('ledger_inv_balance') }}</span><strong class="ledger-num d-block">{{ money(invoice.balance_due) }}</strong></div>
        </div>
        <div class="mt-1">
          <b-link
            v-if="journalId && canOpenJournal"
            class="mr-1"
            :to="journalRoute(journalId)"
          >
            {{ $t('ledger_journal_open') }}
          </b-link>
          <b-link
            v-if="reversalId && canOpenJournal"
            :to="journalRoute(reversalId)"
          >
            {{ $t('ledger_depr_reversal_journal') }}
          </b-link>
          <b-link
            v-if="invoice.customer_id && hasPermission('view invoices')"
            class="d-inline-block mr-1"
            :to="{ name: 'ledger-customer-statement', params: { id: invoice.customer_id } }"
          >
            {{ $t('ledger_inv_statement') }}
          </b-link>
        </div>
        <b-alert
          v-if="showLinksHint"
          show
          variant="warning"
          class="mt-1 mb-0"
        >
          <b-link :to="{ name: 'ledger-links' }">
            {{ $t('ledger_links_title') }}
          </b-link>
        </b-alert>
      </b-card>

      <b-card
        v-if="payOpen && canCollect"
        class="rounded-lg"
      >
        <h5>{{ $t('ledger_inv_collect') }}</h5>
        <p class="text-muted">
          {{ $t('ledger_inv_pay_hint', { code: customerGlCode }) }}
        </p>
        <b-row>
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group :label="$t('ledger_payroll_till')">
              <v-select
                v-model="payment.account_id"
                :dir="isRtl ? 'rtl' : 'ltr'"
                :options="tills"
                :reduce="option => option.id"
                :get-option-label="tillLabel"
                :loading="loadingTills"
                class="ledger-account-select"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_assets_amount')">
              <b-form-input
                v-model.number="payment.amount"
                type="number"
                min="0.01"
                step="0.01"
                :max="Number(invoice.balance_due)"
              />
            </b-form-group>
          </b-col>
          <b-col
            cols="6"
            md="3"
          >
            <b-form-group :label="$t('ledger_date')">
              <b-form-input
                v-model="payment.payment_date"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group :label="$t('ledger_assets_notes')">
              <b-form-input v-model="payment.notes" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-button
          variant="success"
          class="rounded-pill"
          :disabled="acting || !payment.account_id || !validPayment"
          @click="submitPayment"
        >
          {{ $t('ledger_inv_collect') }}
        </b-button>
      </b-card>

      <b-card class="rounded-lg">
        <h5>{{ $t('ledger_inv_lines') }}</h5>
        <p
          v-if="hasItemLines"
          class="text-muted small"
        >
          {{ $t('ledger_inv_stock_note') }}
        </p>
        <div
          v-for="line in invoice.lines || []"
          :key="line.id"
          class="d-flex justify-content-between ledger-inv-card"
        >
          <div>
            <b-badge variant="light-primary">
              {{ enumLabel(line.type, $i18n.locale) }}
            </b-badge>
            <div>{{ line.description }}</div>
            <div
              v-if="line.item"
              class="small mt-25"
            >
              <b-link
                v-if="hasPermission('view items')"
                :to="{ name: 'ledger-items-view', params: { id: line.item.id } }"
              >
                {{ line.item.code }} — {{ line.item.name }}
              </b-link>
              <span v-else>{{ line.item.code }} — {{ line.item.name }}</span>
              <b-badge
                variant="light-primary"
                class="ml-50"
              >
                {{ itemKindLabel(line.item.kind) }}
              </b-badge>
            </div>
            <div
              v-else-if="line.item_id"
              class="small text-muted"
            >
              #{{ line.item_id }}
            </div>
            <div
              v-if="line.warehouse"
              class="small text-muted"
            >
              <b-link
                v-if="hasPermission('view warehouses')"
                :to="{ name: 'ledger-warehouses-view', params: { id: line.warehouse.id } }"
              >
                {{ line.warehouse.code }} — {{ line.warehouse.name }}
              </b-link>
              <span v-else>{{ line.warehouse.code }} — {{ line.warehouse.name }}</span>
            </div>
            <div
              v-else-if="line.warehouse_id"
              class="small text-muted"
            >
              #{{ line.warehouse_id }}
            </div>
            <div
              v-if="line.item"
              class="small text-muted ledger-num"
            >
              {{ qty(line.quantity) }} × {{ money(line.unit_price) }}
            </div>
          </div>
          <span class="ledger-num">{{ money(line.amount) }}</span>
        </div>
      </b-card>

      <b-card class="rounded-lg">
        <h5>{{ $t('ledger_inv_payments') }}</h5>
        <div
          v-if="!(invoice.payments || []).length"
          class="text-muted"
        >
          {{ $t('ledger_inv_no_payments') }}
        </div>
        <article
          v-for="paymentRow in invoice.payments || []"
          :key="paymentRow.id"
          class="ledger-inv-card"
        >
          <div class="d-flex justify-content-between">
            <span>{{ enumLabel(paymentRow.kind, $i18n.locale) }}</span>
            <strong class="ledger-num">{{ money(paymentRow.amount) }}</strong>
          </div>
          <div class="text-muted small">
            {{ paymentRow.payment_date }}
            <span v-if="paymentRow.transaction_id"> · {{ $t('ledger_inv_from_transaction') }}</span>
          </div>
          <div class="small">
            {{ $t('ledger_pay_from', { from: invoicePayFrom(paymentRow) }) }}
          </div>
          <div class="small">
            {{ $t('ledger_pay_to', { to: invoicePayTo(paymentRow) }) }}
          </div>
          <b-link
            v-if="paymentRow.journal_entry_id && canOpenJournal"
            :to="journalRoute(paymentRow.journal_entry_id)"
          >
            {{ $t('ledger_journal_open') }}
          </b-link>
        </article>
      </b-card>

      <div
        v-if="showMobileActions"
        class="ledger-inv-sticky d-md-none"
      >
        <div class="ledger-num small mb-50">
          {{ $t('ledger_inv_balance') }} {{ money(invoice.balance_due) }}
        </div>
        <b-button
          v-if="canIssue"
          block
          variant="primary"
          class="rounded-pill mb-50"
          @click="confirmIssue"
        >
          {{ $t('ledger_inv_issue') }}
        </b-button>
        <b-button
          v-if="canCollect"
          block
          variant="success"
          class="rounded-pill"
          @click="payOpen = true"
        >
          {{ $t('ledger_inv_collect') }}
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard, BBadge, BButton, BLink, BAlert, BRow, BCol, BFormGroup, BFormInput, BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Swal from 'sweetalert2'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  getInvoice, issueInvoice, recordInvoicePayment, applyInvoiceAdvance, cancelInvoice, deleteInvoice, getCustomerAdvanceBalance,
} from '@/api/accounting/invoices'
import { getContract } from '@/api/accounting/contracts'
import { getProjectCost } from '@/api/accounting/projectCost'
import { fetchAllOperationalAccounts } from '@/api/accounting/accounts'
import {
  oneFrom, enumValue, enumLabel, formatLedgerMoney, formatLedgerQty, roundMoney, todayIso, apiErrorMessage, ledgerMessageKey,
  glAccountCode, glAccountText,
} from '@/utils/ledger'

const STATUS_VARIANT = {
  draft: 'secondary', issued: 'info', partially_paid: 'warning', paid: 'success', cancelled: 'dark',
}

export default {
  name: 'InvoiceShow',
  components: {
    BCard, BBadge, BButton, BLink, BAlert, BRow, BCol, BFormGroup, BFormInput, BSpinner, vSelect,
  },
  data() {
    return {
      loading: false,
      acting: false,
      payOpen: false,
      loadingTills: false,
      tills: [],
      invoice: null,
      advanceBalance: 0,
      showLinksHint: false,
      billingEnded: false,
      payment: { account_id: null, amount: null, payment_date: todayIso(), notes: '' },
    }
  },
  computed: {
    isRtl() {
      return this.$store.state.appConfig.layout.isRTL
    },
    statusValue() {
      return enumValue(this.invoice && this.invoice.status)
    },
    kindValue() {
      return enumValue(this.invoice && this.invoice.kind) || 'standard'
    },
    statusVariant() {
      return STATUS_VARIANT[this.statusValue] || 'secondary'
    },
    openForPayment() {
      return this.statusValue === 'issued' || this.statusValue === 'partially_paid'
    },
    nothingSettled() {
      const paid = Number(this.invoice && this.invoice.paid_amount) || 0
      const applied = Number(this.invoice && this.invoice.advance_applied) || 0
      const payments = (this.invoice && this.invoice.payments) || []
      return paid <= 0 && applied <= 0 && !payments.length
    },
    canEditDraft() {
      return this.statusValue === 'draft' && this.kindValue === 'standard' && this.hasPermission('edit invoices')
    },
    canDelete() {
      return this.statusValue === 'draft' && this.hasPermission('delete invoices')
    },
    canIssue() {
      return this.statusValue === 'draft' && this.hasPermission('send invoices')
    },
    canCollect() {
      return this.openForPayment && this.hasPermission('mark-paid invoices')
    },
    canCancel() {
      return this.openForPayment && this.nothingSettled && this.hasPermission('edit invoices')
    },
    canOpenJournal() {
      return this.hasPermission('view journal_entries')
    },
    showMobileActions() {
      return this.canIssue || this.canCollect || this.canCancel || this.canDelete
    },
    journalId() {
      return this.invoice && (this.invoice.journal_entry_id || (this.invoice.journal_entry && this.invoice.journal_entry.id))
    },
    reversalId() {
      return this.invoice && (this.invoice.reversal_journal_entry_id || (this.invoice.reversal_journal_entry && this.invoice.reversal_journal_entry.id))
    },
    customerName() {
      return (this.invoice.customer && this.invoice.customer.name) || '—'
    },
    customerGlCode() {
      return glAccountCode(this.invoice && this.invoice.customer) || '—'
    },
    projectLabel() {
      const project = this.invoice.project
      if (!project) return '—'
      return project.project_number || project.name || '—'
    },
    activityLabel() {
      const key = this.invoice.gl_activity
      return key ? this.$t(`ledger_inv_activity_${key}`) : ''
    },
    validPayment() {
      const amount = Number(this.payment.amount)
      const due = Number(this.invoice.balance_due) || 0
      return amount > 0 && amount <= due + 0.001
    },
    hasItemLines() {
      return ((this.invoice && this.invoice.lines) || []).some(line => line.item_id || (line.item && line.item.id))
    },
  },
  watch: {
    '$route.params.id': 'load',
    canCollect(value) {
      if (value) {
        this.loadTills()
        this.loadAdvance()
      }
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    enumLabel,
    money(value) {
      return formatLedgerMoney(value || 0, this.$i18n.locale)
    },
    qty(value) {
      return formatLedgerQty(value || 0, this.$i18n.locale)
    },
    itemKindLabel(kind) {
      if (kind && typeof kind === 'object') return kind.label_ar || kind.label || kind.value || ''
      return kind ? String(kind) : ''
    },
    tillLabel(account) {
      if (!account) return ''
      return account.code ? `${account.code} — ${account.name || ''}` : (account.name || '')
    },
    journalRoute(id) {
      return { name: 'ledger-journals-view', params: { id } }
    },
    toast(title, variant) {
      const icons = { success: 'CheckIcon', warning: 'AlertTriangleIcon', danger: 'AlertCircleIcon' }
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: icons[variant] || 'InfoIcon', variant },
      })
    },
    noteUnmapped(error) {
      const message = apiErrorMessage(error, this.$t('ledger_error'))
      const key = ledgerMessageKey(message)
      if (key) {
        this.toast(this.$t(key), error?.response?.status === 422 ? 'warning' : 'danger')
        return
      }
      const text = String(message).toLowerCase()
      this.showLinksHint = (text.includes('ربط') || text.includes('unmapped') || text.includes('link'))
        && this.hasPermission('edit gl_links')
      this.toast(message, error?.response?.status === 422 ? 'warning' : 'danger')
    },
    async load() {
      this.loading = true
      try {
        const res = await getInvoice(this.$route.params.id)
        this.invoice = oneFrom(res)
        this.payment.amount = Number(this.invoice.balance_due) || 0
        if (this.canCollect) {
          this.loadTills()
          this.loadAdvance()
        }
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async loadTills() {
      if (this.tills.length || this.loadingTills) return
      this.loadingTills = true
      try {
        const rows = await fetchAllOperationalAccounts({ status: 'active', per_page: 100 })
        this.tills = rows.filter(account => ['cash', 'bank', 'petty_cash'].includes(enumValue(account.type)))
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loadingTills = false
      }
    },
    async loadAdvance() {
      if (!this.invoice || !this.invoice.customer_id) return
      try {
        const res = await getCustomerAdvanceBalance(this.invoice.customer_id)
        const data = oneFrom(res)
        this.advanceBalance = Number(data.advance_balance) || 0
      } catch (error) {
        this.advanceBalance = 0
      }
    },
    issueLines() {
      const kind = this.kindValue
      if (kind === 'progress_billing') {
        const lines = [this.$t('ledger_ct_dr_ar', { code: this.customerGlCode, amount: this.money(this.invoice.collectible_amount) })]
        const retention = Number(this.invoice.retention_amount) || 0
        if (retention) lines.push(this.$t('ledger_ct_dr_retention', { amount: this.money(retention) }))
        const creditKey = this.billingEnded ? 'ledger_ct_cr_unbilled' : 'ledger_ct_cr_billings'
        lines.push(this.$t(creditKey, { amount: this.money(this.invoice.total_amount) }))
        return lines
      }
      if (kind === 'retention_release') {
        const amount = this.invoice.total_amount
        return [
          this.$t('ledger_ct_dr_ar', { code: this.customerGlCode, amount: this.money(amount) }),
          this.$t('ledger_ct_cr_retention', { amount: this.money(amount) }),
        ]
      }
      const code = this.invoice.revenue_code || (this.invoice.gl_activity === 'licensing' ? '4130' : '4110')
      const lines = [this.$t('ledger_inv_dr_ar', { code: this.customerGlCode, amount: this.money(this.invoice.total_amount) })]
      const service = Number(this.invoice.subtotal) || 0
      if (service) lines.push(this.$t('ledger_inv_cr_revenue', { code, amount: this.money(service) }))
      const fees = Number(this.invoice.government_fees_total) || 0
      if (fees) lines.push(this.$t('ledger_inv_cr_fees', { amount: this.money(fees) }))
      const discount = Number(this.invoice.discount_amount) || 0
      if (discount) lines.push(this.$t('ledger_inv_dr_discount', { amount: this.money(discount) }))
      return lines
    },
    localBillingEnded() {
      const invoice = this.invoice || {}
      const contract = invoice.contract || invoice.project_contract || null
      const position = invoice.position || (contract && contract.position) || null
      if (position && typeof position.ended === 'boolean') return position.ended
      if (contract && typeof contract.ended === 'boolean') return contract.ended
      return null
    },
    async resolveBillingEnded() {
      if (this.kindValue !== 'progress_billing' || !this.invoice) return false
      const local = this.localBillingEnded()
      if (local !== null) return local
      const projectId = this.invoice.project_id || (this.invoice.project && this.invoice.project.id)
      if (projectId) {
        let cost = null
        try {
          cost = oneFrom(await getProjectCost(projectId))
        } catch (error) {
          cost = null
        }
        const contract = cost && cost.contract
        if (contract && typeof contract.ended === 'boolean') return contract.ended
        if (contract && contract.position && typeof contract.position.ended === 'boolean') {
          return contract.position.ended
        }
      }
      const contractId = this.invoice.project_contract_id
        || this.invoice.contract_id
        || (this.invoice.contract && this.invoice.contract.id)
      if (contractId) {
        try {
          const contract = oneFrom(await getContract(contractId))
          const position = (contract && contract.position) || {}
          if (typeof position.ended === 'boolean') return position.ended
        } catch (error) {
          return false
        }
      }
      return false
    },
    async confirmIssue() {
      this.billingEnded = await this.resolveBillingEnded()
      const result = await Swal.fire({
        title: this.$t('ledger_inv_issue'),
        html: [
          ...this.issueLines(),
          ...(this.hasItemLines ? [this.$t('ledger_inv_stock_issue')] : []),
        ].map(line => `<div>${line}</div>`).join(''),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_issue'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        await issueInvoice(this.invoice.id)
        this.toast(this.$t('ledger_inv_issued'), 'success')
        await this.load()
      } catch (error) {
        this.noteUnmapped(error)
      } finally {
        this.acting = false
      }
    },
    selectedTill() {
      return (this.tills || []).find(till => till.id === this.payment.account_id) || null
    },
    partyChart() {
      return glAccountText(this.invoice && this.invoice.customer) || '—'
    },
    chartOf(record) {
      return glAccountText(record) || '—'
    },
    isAdvancePayment(paymentRow) {
      return enumValue(paymentRow && paymentRow.kind) === 'advance'
    },
    invoicePayFrom(paymentRow) {
      if (this.isAdvancePayment(paymentRow)) return '2170'
      return this.partyChart()
    },
    invoicePayTo(paymentRow) {
      if (this.isAdvancePayment(paymentRow)) return this.partyChart()
      return this.chartOf(paymentRow && paymentRow.account)
    },
    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    },
    async submitPayment() {
      if (!this.validPayment) {
        this.toast(this.$t('ledger_inv_overpay'), 'warning')
        return
      }
      const from = this.partyChart()
      const to = this.chartOf(this.selectedTill())
      const result = await Swal.fire({
        title: this.$t('ledger_inv_collect'),
        html: [
          this.$t('ledger_pay_from', { from: this.escapeHtml(from) }),
          this.$t('ledger_pay_to', { to: this.escapeHtml(to) }),
          `${this.escapeHtml(this.money(this.payment.amount))} · ${this.escapeHtml(this.payment.payment_date || '')}`,
        ].map(line => `<div>${line}</div>`).join(''),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_collect'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-success', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        await recordInvoicePayment(this.invoice.id, {
          account_id: this.payment.account_id,
          amount: roundMoney(this.payment.amount),
          payment_date: this.payment.payment_date,
          notes: String(this.payment.notes || '').trim() || undefined,
        })
        this.payOpen = false
        this.toast(this.$t('ledger_inv_collected'), 'success')
        await this.load()
      } catch (error) {
        this.noteUnmapped(error)
      } finally {
        this.acting = false
      }
    },
    async openAdvance() {
      await this.loadAdvance()
      if (this.advanceBalance <= 0) return
      const max = Math.min(this.advanceBalance, Number(this.invoice.balance_due) || 0)
      const result = await Swal.fire({
        title: this.$t('ledger_inv_apply_advance'),
        html: [
          this.$t('ledger_pay_from', { from: '2170' }),
          this.$t('ledger_pay_to', { to: this.escapeHtml(this.partyChart()) }),
          this.$t('ledger_inv_advance_hint', { code: this.customerGlCode, amount: this.money(max) }),
        ].map(line => `<div>${line}</div>`).join(''),
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_apply_advance'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-primary', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      this.acting = true
      try {
        await applyInvoiceAdvance(this.invoice.id, {})
        this.toast(this.$t('ledger_inv_advance_done'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
      } finally {
        this.acting = false
      }
    },
    async confirmCancel() {
      const result = await Swal.fire({
        text: this.hasItemLines
          ? `${this.$t('ledger_inv_cancel_confirm')} ${this.$t('ledger_inv_stock_cancel')}`
          : this.$t('ledger_inv_cancel_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_inv_cancel'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await cancelInvoice(this.invoice.id)
        this.toast(this.$t('ledger_inv_cancelled'), 'success')
        await this.load()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
      }
    },
    async confirmDelete() {
      const result = await Swal.fire({
        text: this.hasItemLines
          ? `${this.$t('ledger_inv_delete_confirm')} ${this.$t('ledger_inv_stock_delete')}`
          : this.$t('ledger_inv_delete_confirm'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ledger_assets_delete'),
        cancelButtonText: this.$t('ledger_cancel'),
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      })
      if (!result.isConfirmed) return
      try {
        await deleteInvoice(this.invoice.id)
        this.$router.push({ name: 'ledger-invoices' })
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'warning')
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
.ledger-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ledger-inv-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
@media (min-width: 768px) { .ledger-inv-kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); } }
.ledger-inv-card { border-bottom: 1px solid #eee; padding: 0.75rem 0.15rem; }
.ledger-inv-sticky {
  position: fixed; bottom: 0; inset-inline: 0; z-index: 20; background: #fff;
  border-top: 1px solid #e6e6e6; padding: 0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom));
}
</style>
